/**
 * Versandrueckmeldung am Lieferschein.
 *
 * Das Lager traegt nach dem Packen ein, was rausgegangen ist: Versandtag,
 * Dienst, Sendungsnummer, Packstuecke, Gewicht. Mehrere Meldungen je
 * Lieferschein sind moeglich (Teilversand). Bekannte Dienste bekommen einen
 * Link zur Sendungsverfolgung. Der Vertrieb sieht "Versandt" in Liste und Akte
 * und erhaelt einen Hinweis ueber die Glocke.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CARRIERS, carrierLabel, formatDate, formatQuantity, today, trackingLink } from '@erp/shared';

import { api, ApiError } from '../api/client';
import type { Shipment } from '../api/types';
import { useAuth } from '../auth';
import { useToast } from './toast';
import { Badge, Field, NumericInput, Select, TextInput } from './ui';

export function ShipmentCard({
  documentId,
  documentNumber,
  defaultPackages,
  defaultWeightGrams,
}: {
  documentId: string;
  documentNumber: string | null;
  /** Vorbelegung aus den Packdaten des Lieferscheins (Rueckmeldung vom Lager). */
  defaultPackages?: number;
  defaultWeightGrams?: number;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const mayReport = can('stock:write') || can('sales:write');

  const [shippedOn, setShippedOn] = useState(today());
  const [carrier, setCarrier] = useState('dhl');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [packages, setPackages] = useState(defaultPackages && defaultPackages > 0 ? defaultPackages : 1);
  /** Gewicht in Gramm = Tausendstel Kilogramm, passt zur Mengeneingabe. */
  const [weightGrams, setWeightGrams] = useState(defaultWeightGrams ?? 0);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['shipments', documentId],
    queryFn: () => api.get<{ shipments: Shipment[] }>(`/api/documents/${documentId}/shipments`),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['shipments', documentId] });
    queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    queryClient.invalidateQueries({ queryKey: ['documents'] });
  };

  const report = useMutation({
    mutationFn: () =>
      api.post<{ shipment: Shipment }>(`/api/documents/${documentId}/shipments`, {
        shippedOn,
        carrier,
        trackingNumber: trackingNumber || null,
        packages,
        weightGrams,
        note: note || null,
      }),
    onSuccess: () => {
      invalidate();
      setTrackingNumber('');
      setNote('');
      setErrors({});
      toast.success(`Versand für ${documentNumber ?? 'den Lieferschein'} gemeldet.`);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Die Versandrückmeldung ist fehlgeschlagen.');
      }
    },
  });

  const remove = useMutation({
    mutationFn: (shipmentId: string) => api.delete(`/api/documents/${documentId}/shipments/${shipmentId}`),
    onSuccess: () => {
      invalidate();
      toast.info('Versandrückmeldung entfernt.');
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : 'Entfernen fehlgeschlagen.'),
  });

  const shipments = data?.shipments ?? [];

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header">
        <div>
          <h2>Versand</h2>
          <div className="faint" style={{ fontSize: 12.5 }}>
            Rückmeldung des Lagers: Was ist wann mit wem rausgegangen?
          </div>
        </div>
        {shipments.length > 0 ? <Badge kind="success">Versandt</Badge> : <Badge>Noch nicht versandt</Badge>}
      </div>

      {isLoading ? null : shipments.length > 0 ? (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Versandtag</th>
                <th>Dienst</th>
                <th>Sendungsnummer</th>
                <th className="numeric">Packstücke</th>
                <th className="numeric">Gewicht</th>
                <th>Bemerkung</th>
                <th>Gemeldet von</th>
                {mayReport ? <th className="shrink"></th> : null}
              </tr>
            </thead>
            <tbody>
              {shipments.map((shipment) => {
                const link = trackingLink(shipment.carrier, shipment.trackingNumber);
                return (
                  <tr key={shipment.id}>
                    <td>{formatDate(shipment.shippedOn)}</td>
                    <td>{carrierLabel(shipment.carrier)}</td>
                    <td className="mono">
                      {link ? (
                        <a href={link} target="_blank" rel="noreferrer" title="Sendungsverfolgung öffnen">
                          {shipment.trackingNumber} ↗
                        </a>
                      ) : (
                        shipment.trackingNumber ?? <span className="faint">–</span>
                      )}
                    </td>
                    <td className="numeric">{shipment.packages}</td>
                    <td className="numeric">{shipment.weightGrams > 0 ? `${formatQuantity(shipment.weightGrams)} kg` : <span className="faint">–</span>}</td>
                    <td className="faint">{shipment.note ?? ''}</td>
                    <td className="faint">{shipment.createdByName ?? ''}</td>
                    {mayReport ? (
                      <td>
                        <button
                          type="button"
                          className="btn btn-quiet btn-sm"
                          title="Rückmeldung entfernen (Fehleingabe)"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(shipment.id)}
                        >
                          ✕
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {mayReport ? (
        <div className="card-body">
          <div className="form-grid">
            <Field label="Versandtag" span={2} error={errors.shippedOn}>
              <TextInput type="date" value={shippedOn} onChange={setShippedOn} />
            </Field>
            <Field label="Versanddienst" span={3} error={errors.carrier}>
              <Select value={carrier} onChange={setCarrier} options={CARRIERS.map((entry) => ({ value: entry.code, label: entry.label }))} />
            </Field>
            <Field label="Sendungsnummer" span={3} error={errors.trackingNumber} hint="leer lassen bei Abholung oder eigener Zustellung">
              <TextInput value={trackingNumber} onChange={setTrackingNumber} placeholder="z. B. 00340434161094015902" />
            </Field>
            <Field label="Packstücke" span={1} error={errors.packages}>
              <input
                autoComplete="off"
                className="input numeric"
                type="number"
                min={1}
                max={999}
                value={packages}
                onChange={(event) => setPackages(Math.max(1, Number(event.target.value) || 1))}
              />
            </Field>
            <Field label="Gewicht (kg)" span={2} error={errors.weightGrams}>
              <NumericInput kind="quantity" value={weightGrams} onChange={setWeightGrams} />
            </Field>
            <Field label="Bemerkung" span={9} error={errors.note}>
              <TextInput value={note} onChange={setNote} placeholder="z. B. Palette 2 folgt am Montag" />
            </Field>
            <Field label=" " span={3}>
              <button type="button" className="btn btn-primary" disabled={report.isPending} onClick={() => report.mutate()}>
                {report.isPending ? 'Wird gemeldet …' : 'Versand melden'}
              </button>
            </Field>
          </div>
        </div>
      ) : null}
    </div>
  );
}
